import type { ComputeProfileId } from "../lib/computeProfile";
import type { AwsRegionId } from "../lib/awsRegion";
import { awsRegionFactor } from "../lib/awsRegion";
import type { SavingsLevers } from "../lib/savingsLevers";
import type { AudienceMode } from "../lib/audienceMode";
import type { Locale } from "./locale";
import { numberLocaleFor } from "./locale";

const DAYS_PER_MONTH = 30;

export function fmtInt(n: number, locale: Locale): string {
  return Math.round(n).toLocaleString(numberLocaleFor(locale));
}

export function costAssumptions(
  locale: Locale,
  audience: AudienceMode = "technical",
): string[] {
  if (locale === "uk") {
    if (audience === "simple") {
      return [
        `Рахунок будується на типовому місяці (~${DAYS_PER_MONTH} днів) і публічних орієнтирах цін — не як фактура від Amazon.`,
        "Під «хмарою» маємо на увазі автоматичне масштабування сервісів під навантаження; конкретні секунди роботи сервера в моделі спрощені.",
        "Витрати на «розумний помічник» (AI) залежать від того, скільки разів користувачі звертаються до нього і наскільки довгі відповіді.",
        "Чим ширший діапазон у підсумку — тим більше невизначеності в реальному житті (акції, регіон, знижки). Звіряй з фактичним біллом.",
      ];
    }
    return [
      `${DAYS_PER_MONTH} днів/міс, on-demand pricing, регіон us-east-1 (орієнтир).`,
      "Lambda: 512 MB, ~250 ms середній час на HTTP handler; GB·s і per-request з публічних тарифів.",
      "LLM: змішана ціна input/output на кшталт економ-класу GPT; модель не підтягує live price API.",
      "Технічний режим: вужчий діапазон навколо point estimate — все одно індикативно, без SLA на точність.",
    ];
  }
  if (locale === "es") {
    if (audience === "simple") {
      return [
        `Cálculo con mes típico (~${DAYS_PER_MONTH} días) y precios públicos de referencia — no es tu factura real.`,
        "«Nube» aquí significa servicios que escalan solos; los tiempos de cómputo del modelo están simplificados.",
        "El coste de la IA depende de cuántas veces se usa y del tamaño de las respuestas.",
        "Un rango amplio refleja incertidumbre real (región, descuentos, tráfico). Contrasta con facturas reales.",
      ];
    }
    return [
      `${DAYS_PER_MONTH} días/mes, on-demand, región us-east-1 como referencia.`,
      "Lambda: 512 MB, ~250 ms medio por petición HTTP; GB·s y requests según listas públicas.",
      "LLM: precio mezclado input/output tipo tier económico de GPT; sin API de precios en vivo.",
      "Modo técnico: banda más estrecha alrededor del punto — sigue siendo orientativo.",
    ];
  }
  if (audience === "simple") {
    return [
      `We use a typical month (~${DAYS_PER_MONTH} days) and public price hints — not your real AWS invoice.`,
      "“Cloud” here means services that scale automatically; server runtimes in the model are simplified.",
      "AI spend depends on how often users call it and how long replies are.",
      "A wide range reflects real-world uncertainty (region, discounts, traffic). Always verify against actual bills.",
    ];
  }
  return [
    `${DAYS_PER_MONTH} days/mo, on-demand pricing, us-east-1 region (reference).`,
    "Lambda: 512 MB, ~250 ms average HTTP handler time; GB·s and per-request from public SKUs.",
    "LLM: blended input/output pricing similar to economy-tier GPT; no live pricing API.",
    "Technical mode: tighter band around the point estimate — still indicative, not a precision guarantee.",
  ];
}

export function regionAssumptionNote(locale: Locale, region: AwsRegionId): string {
  const f = awsRegionFactor(region);
  if (locale === "uk") {
    return `Регіон AWS ${region}: множник ×${f.toFixed(2)} до всіх позицій (наближено vs us-east-1).`;
  }
  return `AWS region ${region}: ×${f.toFixed(2)} applied to all line items (approx. vs us-east-1).`;
}

/** Extra assumption when GPU-style profile is selected (null for CPU baseline). */
export function computeProfileAssumptionNote(
  locale: Locale,
  profile: ComputeProfileId,
): string | null {
  if (profile !== "gpu") return null;
  if (locale === "uk") {
    return "Профіль GPU: Lambda та egress збільшені наближено (важчий compute / більші відповіді) — без окремого рядка SageMaker чи EC2 з GPU.";
  }
  if (locale === "es") {
    return "Perfil GPU: Lambda y egress aumentados de forma aproximada (más cómputo) — sin partida separada de SageMaker o EC2 con GPU.";
  }
  return "GPU profile: Lambda and egress scaled up heuristically (heavier compute) — no separate SageMaker or EC2 GPU line item.";
}

export function costLineCopy(
  locale: Locale,
  ctx: {
    monthlyRequests: number;
    lambdaInvocations: number;
    lambdaMemoryGb: number;
    readsPerMonth: number;
    writesPerMonth: number;
    aiCallsMonthly: number;
    avgPromptTokens: number;
    avgCompletionTokens: number;
    s3Gb: number;
  },
): Record<
  "apigw" | "lambda" | "dynamo" | "s3" | "llm" | "cw" | "xfer",
  { label: string; detail: string }
> {
  const f = (n: number) => fmtInt(n, locale);
  const { monthlyRequests, lambdaInvocations, lambdaMemoryGb } = ctx;
  const { readsPerMonth, writesPerMonth, aiCallsMonthly } = ctx;
  const { avgPromptTokens, avgCompletionTokens, s3Gb } = ctx;
  const s3GbText =
    s3Gb > 0 && s3Gb < 1 ? s3Gb.toFixed(2) : f(Math.max(0, Math.round(s3Gb)));

  if (locale === "uk") {
    return {
      apigw: {
        label: "API Gateway",
        detail: `${f(monthlyRequests)} викликів/міс`,
      },
      lambda: {
        label: "Lambda",
        detail: `${f(lambdaInvocations)} invocations, ~${lambdaMemoryGb} GB·s/req`,
      },
      dynamo: {
        label: "DynamoDB (on-demand)",
        detail: `~${f(readsPerMonth)} RCU-екв. reads, ~${f(writesPerMonth)} WCU-екв. writes`,
      },
      s3: {
        label: "Amazon S3 (файли)",
        detail: `~${s3GbText} ГБ Standard (об'єктне сховище)`,
      },
      llm: {
        label: "OpenAI / Bedrock (LLM)",
        detail: `~${f(aiCallsMonthly)} AI-викликів, ${avgPromptTokens}/${avgCompletionTokens} tok/call`,
      },
      cw: {
        label: "CloudWatch + метрики",
        detail: "логи + базові метрики",
      },
      xfer: {
        label: "Data transfer (egress)",
        detail: "орієнтовний outbound",
      },
    };
  }

  return {
    apigw: {
      label: "API Gateway",
      detail: `${f(monthlyRequests)} calls/mo`,
    },
    lambda: {
      label: "Lambda",
      detail: `${f(lambdaInvocations)} invocations, ~${lambdaMemoryGb} GB·s/req`,
    },
    dynamo: {
      label: "DynamoDB (on-demand)",
      detail: `~${f(readsPerMonth)} RCU-equiv reads, ~${f(writesPerMonth)} WCU-equiv writes`,
    },
    s3: {
      label: "Amazon S3 (object storage)",
      detail: `~${s3GbText} GB Standard (files / uploads)`,
    },
    llm: {
      label: "OpenAI / Bedrock (LLM)",
      detail: `~${f(aiCallsMonthly)} AI calls, ${avgPromptTokens}/${avgCompletionTokens} tok/call`,
    },
    cw: {
      label: "CloudWatch + metrics",
      detail: "logs + basic metrics",
    },
    xfer: {
      label: "Data transfer (egress)",
      detail: "approximate outbound",
    },
  };
}

/** Extra assumption bullets when savings levers are enabled. */
export function savingsLeverNotes(locale: Locale, s: SavingsLevers): string[] {
  const out: string[] = [];
  if (s.spotOrPreemptibleCompute) {
    out.push(
      locale === "uk"
        ? "Міра економії: Spot / preemptible compute — знижка ~58% на GB·s у складі Lambda (виклики без змін). Орієнтир для Batch/Fargate Spot тощо; у Lambda окремого Spot SKU немає."
        : "Savings lever: Spot / preemptible compute — ~58% off Lambda GB·s (request charges unchanged). Indicative for Batch/Fargate Spot; Lambda has no Spot SKU.",
    );
  }
  if (s.dynamoCommittedCapacity) {
    out.push(
      locale === "uk"
        ? "Міра економії: committed / provisioned Dynamo — ~22% знижка на рядок DynamoDB (стабільне навантаження)."
        : "Savings lever: committed / provisioned Dynamo — ~22% off the DynamoDB line (steady traffic).",
    );
  }
  if (s.llmEconomyTier) {
    out.push(
      locale === "uk"
        ? "Міра економії: дешевший LLM-шар — ~28% знижка на рядок LLM (batch, менша модель, кеш)."
        : locale === "es"
          ? "Palanca: LLM más barato — ~28% menos en la línea LLM (batch, modelo menor, caché)."
          : "Savings lever: cheaper LLM tier — ~28% off the LLM line (batch, smaller model, caching).",
    );
  }
  if (s.llmBatchApi) {
    out.push(
      locale === "uk"
        ? "Міра економії: batch / async LLM API — додатково ~32% на рядок LLM порівняно з realtime (індикативно для Batch API, черг)."
        : locale === "es"
          ? "Palanca: API LLM batch/async — ~32% adicional en la línea LLM vs tiempo real (indicativo)."
          : "Savings lever: batch / async LLM API — extra ~32% off the LLM line vs realtime (indicative for batch APIs, queues).",
    );
  }
  if (s.scheduleOffWeekends) {
    out.push(
      locale === "uk"
        ? "Міра економії: вимкнення на вихідних — години EC2/EKS worker і RDS-інстансу в моделі ×5/7 (~пн–пт). Lambda, сховище, Route 53, GuardDuty без змін."
        : "Savings lever: off weekends — EC2, EKS worker, and RDS instance hours in the model ×5/7 (~Mon–Fri). Lambda, storage, Route 53, GuardDuty unchanged.",
    );
  }
  return out;
}
